/**
 * 오프라인 상태 배너 컴포넌트
 * 토스 스타일: 미니멀하고 직관적인 오프라인 상태 표시
 * 설명 없이도 오프라인임을 즉시 인지할 수 있는 디자인
 */

import { useNetworkStatus } from "@/hooks/useNetworkStatus";

interface OfflineBannerProps {
  /** 동기화 대기 중인 변경사항 수 */
  pendingChanges?: number;
  /** 캐시된 데이터 표시 여부 (온라인이어도 캐시 배너 표시) */
  showCacheIndicator?: boolean;
}

export function OfflineBanner({
  pendingChanges = 0,
  showCacheIndicator = false,
}: OfflineBannerProps) {
  const { isOffline, isLoading } = useNetworkStatus();

  if (isLoading || (!isOffline && !showCacheIndicator)) {
    return null;
  }

  // 캐시 모드 (온라인이지만 캐시된 데이터 사용 중)
  if (!isOffline && showCacheIndicator) {
    return (
      <div className="mb-4 flex items-center gap-3 rounded-2xl bg-slate-100 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200">
          <svg
            className="h-4 w-4 text-slate-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <span className="text-[15px] font-medium text-slate-600">저장된 데이터</span>
      </div>
    );
  }

  // 오프라인 모드 - 토스 스타일
  return (
    <div className="mb-4">
      {/* 메인 오프라인 인디케이터 */}
      <div className="flex items-center gap-3 rounded-2xl bg-slate-900 px-4 py-3.5">
        {/* 끊어진 연결 아이콘 - 애니메이션 펄스 */}
        <div className="relative">
          <div
            className="absolute inset-0 animate-ping rounded-full bg-slate-400 opacity-20"
            style={{ animationDuration: "2s" }}
          />
          <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-slate-700">
            {/* 커스텀 오프라인 아이콘 - 끊어진 Wi-Fi */}
            <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none">
              {/* Wi-Fi 신호 (흐릿하게) */}
              <path
                d="M12 20h.01M8.53 16.11a6 6 0 016.95 0M5.64 12.45a10 10 0 0112.72 0M2.1 8.82a14 14 0 0119.8 0"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.3"
              />
              {/* 대각선 X */}
              <path d="M4 4l16 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
        </div>

        <div className="flex-1">
          <p className="text-[15px] font-semibold text-white">오프라인</p>
          <p className="text-[13px] text-slate-400">저장된 프로젝트만 볼 수 있어요</p>
        </div>

        {/* 대기 중인 변경사항 뱃지 */}
        {pendingChanges > 0 && (
          <div className="flex items-center gap-1.5 rounded-full bg-blue-500 px-3 py-1.5">
            <svg
              className="h-3.5 w-3.5 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-[13px] font-bold text-white">{pendingChanges}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default OfflineBanner;
